import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import {
  Trophy,
  Users,
  MessageCircle,
  Hourglass,
  CheckCircle2,
  XCircle,
  TrendingUp,
  RefreshCw,
  Award,
} from "lucide-react";

type Kpis = {
  totalReferrals: number;
  uniqueReferrers: number;
  telegramLinkedReferrals: number;
  conversionRate: number; // % linked / total
  pendingRewards: number;
  approvedRewards: number;
  rejectedRewards: number;
  premiumEnrollmentsFromReferrals: number;
  last30Referrals: number;
  last30Linked: number;
  topReferrers: { user_id: string; name: string; email: string; total: number; linked: number }[];
};

const initial: Kpis = {
  totalReferrals: 0,
  uniqueReferrers: 0,
  telegramLinkedReferrals: 0,
  conversionRate: 0,
  pendingRewards: 0,
  approvedRewards: 0,
  rejectedRewards: 0,
  premiumEnrollmentsFromReferrals: 0,
  last30Referrals: 0,
  last30Linked: 0,
  topReferrers: [],
};

const ReferralKpiPanel = () => {
  const [kpis, setKpis] = useState<Kpis>(initial);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();

      const [refs, linked, pending, approved, rejected, last30, last30LinkedRows] = await Promise.all([
        (supabase as any).from("referrals").select("referrer_user_id, referred_user_id"),
        (supabase as any).from("user_telegram").select("user_id"),
        (supabase as any).from("referral_rewards").select("id", { count: "exact", head: true }).eq("status", "pending"),
        (supabase as any)
          .from("referral_rewards")
          .select("id", { count: "exact", head: true })
          .eq("status", "approved"),
        (supabase as any)
          .from("referral_rewards")
          .select("id", { count: "exact", head: true })
          .eq("status", "rejected"),
        (supabase as any).from("referrals").select("referred_user_id").gte("created_at", since),
        (supabase as any).from("user_telegram").select("user_id").gte("linked_at", since),
      ]);

      const refRows: { referrer_user_id: string; referred_user_id: string }[] = refs.data || [];
      const linkedSet = new Set<string>((linked.data || []).map((r: any) => r.user_id));

      const totalReferrals = refRows.length;
      const uniqueReferrers = new Set(refRows.map((r) => r.referrer_user_id)).size;
      const telegramLinkedReferrals = refRows.filter((r) => linkedSet.has(r.referred_user_id)).length;
      const conversionRate = totalReferrals > 0 ? Math.round((telegramLinkedReferrals / totalReferrals) * 100) : 0;

      // Top referrers
      const buckets = new Map<string, { total: number; linked: number }>();
      for (const r of refRows) {
        const b = buckets.get(r.referrer_user_id) || { total: 0, linked: 0 };
        b.total += 1;
        if (linkedSet.has(r.referred_user_id)) b.linked += 1;
        buckets.set(r.referrer_user_id, b);
      }
      const topIds = [...buckets.entries()]
        .sort((a, b) => b[1].linked - a[1].linked || b[1].total - a[1].total)
        .slice(0, 5)
        .map(([id]) => id);

      let topReferrers: Kpis["topReferrers"] = [];
      if (topIds.length) {
        const { data: profs } = await (supabase as any).from("profiles").select("user_id, full_name").in("user_id", topIds);
        topReferrers = topIds.map((id) => {
          const b = buckets.get(id)!;
          const p = (profs || []).find((x: any) => x.user_id === id);
          return {
            user_id: id,
            name: p?.full_name || "Unknown",
            email: "",
            total: b.total,
            linked: b.linked,
          };
        });
      }

      // Premium enrollments granted via referral approvals
      const { count: premiumEnrollmentsFromReferrals } = await (supabase as any)
        .from("referral_rewards")
        .select("id", { count: "exact", head: true })
        .eq("status", "approved")
        .eq("is_redeemed", true);

      const last30Refs = (last30.data || []).length;
      const last30LinkedCount = (last30LinkedRows.data || []).length;

      setKpis({
        totalReferrals,
        uniqueReferrers,
        telegramLinkedReferrals,
        conversionRate,
        pendingRewards: pending.count || 0,
        approvedRewards: approved.count || 0,
        rejectedRewards: rejected.count || 0,
        premiumEnrollmentsFromReferrals: premiumEnrollmentsFromReferrals || 0,
        last30Referrals: last30Refs,
        last30Linked: last30LinkedCount,
        topReferrers,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const stats: { label: string; value: string | number; icon: any; tone: string; sub?: string }[] = [
    {
      label: "Total Referrals",
      value: kpis.totalReferrals,
      icon: Users,
      tone: "text-primary",
      sub: `${kpis.uniqueReferrers} unique referrers`,
    },
    {
      label: "Telegram Linked",
      value: kpis.telegramLinkedReferrals,
      icon: MessageCircle,
      tone: "text-emerald-500",
      sub: `${kpis.conversionRate}% conversion`,
    },
    {
      label: "Pending Approval",
      value: kpis.pendingRewards,
      icon: Hourglass,
      tone: "text-amber-500",
      sub: "Awaiting admin review",
    },
    {
      label: "Approved Rewards",
      value: kpis.approvedRewards,
      icon: CheckCircle2,
      tone: "text-emerald-500",
      sub: `${kpis.premiumEnrollmentsFromReferrals} Premium granted`,
    },
    {
      label: "Rejected Rewards",
      value: kpis.rejectedRewards,
      icon: XCircle,
      tone: "text-destructive",
    },
    {
      label: "Last 30 Days",
      value: kpis.last30Referrals,
      icon: TrendingUp,
      tone: "text-primary",
      sub: `${kpis.last30Linked} new Telegram links`,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-muted-foreground">Referral Program KPIs</h3>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {stats.map((s) => {
          const Icon = s.icon;
          return (
            <Card key={s.label}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-muted-foreground">{s.label}</p>
                  <Icon className={`w-4 h-4 ${s.tone}`} />
                </div>
                <p className="text-2xl font-bold">{s.value}</p>
                {s.sub && <p className="text-[11px] text-muted-foreground mt-1">{s.sub}</p>}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Award className="w-4 h-4 text-amber-500" /> Top Referrers
          </CardTitle>
        </CardHeader>
        <CardContent>
          {kpis.topReferrers.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">No referrals yet.</p>
          ) : (
            <div className="space-y-2">
              {kpis.topReferrers.map((r, i) => (
                <div
                  key={r.user_id}
                  className="flex items-center justify-between py-2 px-3 rounded-md border border-border bg-muted/30"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{r.name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{r.user_id.slice(0, 8)}…</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className="text-[10px]">
                      {r.total} invited
                    </Badge>
                    <Badge className="text-[10px] gap-1">
                      <Trophy className="w-3 h-3" /> {r.linked} linked
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReferralKpiPanel;
